import { Injectable, NgZone } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import {
  CHANGE_ACTIVITY,
  ChangeActivityAction,
  ChangeActivityFailureAction,
  ChangeActivitySuccessAction,
  CREATE_DEFAULT_CONFIGURATION,
  CREATE_DEFAULT_CONFIGURATION_SUCCESS,
  CREATE_PROJECT,
  CREATE_PROJECT_SUCCESS,
  CreateDefaultConfigurationAction,
  CreateDefaultConfigurationFailureAction,
  CreateDefaultConfigurationSuccessAction,
  CreateProjectAction,
  CreateProjectFailureAction,
  CreateProjectSuccessAction,
  GET_PROJECT,
  GET_PROJECT_FAILURE,
  GET_PROJECTS_WITH_STATS,
  GET_PROJECTS_WITH_STATS_SUCCESS,
  GetProjectAction,
  GetProjectFailureAction,
  GetProjectSuccessAction,
  GetProjectsWithStatsFailureAction,
  GetProjectsWithStatsSuccessAction,
  ProjectsAction,
  REMOVE_PROJECT,
  REMOVE_PROJECT_SUCCESS,
  RemoveProjectAction,
  RemoveProjectFailureAction,
  RemoveProjectSuccessAction,
  UPDATE_PROJECT_CONFIGURATION,
  UPDATE_PROJECT_CONFIGURATION_SUCCESS,
  UPDATE_PROJECT_STYLE,
  UPDATE_PROJECT_STYLE_SUCCESS,
  UpdateProjectConfigurationAction,
  UpdateProjectConfigurationFailureAction,
  UpdateProjectConfigurationSuccessAction,
  UpdateProjectStyleAction,
  UpdateProjectStyleFailureAction,
  UpdateProjectStyleSuccessAction
} from './projects.action';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ProjectsService } from './projects.service';
import { of } from 'rxjs/observable/of';
import { ProjectWithStatsResponse } from './project';
import { Router } from '@angular/router';
import { IdPayload } from '../shared/domain/IdPayload';
import { Configuration, ConfigurationResponse, SingleProjectResponse } from '../shared/domain/configuration.model';
import { mapResponse, mapSingleProject } from '../shared/domain/configurationMapper';
import { ChangeProjectActivity } from './models/ChangeProjectActivity';
import { CrazyCallConfigService } from '../shared/services/crazycall-config.service';
import { LeadGenerationConfigurationService } from '../shared/services/lead-generation-configuration.service';
import { ToastrService } from 'ngx-toastr';
import { CreateProjectResponse } from '../shared/models/Project';
import { LeadGenerationStylesService } from '../shared/services/lead-generation-styles.service';
import { UpdateStyleRequest } from '../shared/domain/UpdateStyleRequest';
import { TranslateService } from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
import { AccountService } from '../../shared/services/account.service';
import { EMPTY } from 'rxjs';

@Injectable()
export class ProjectsEffects {
  @Effect()
  getProjects$: Observable<ProjectsAction> = this.actions$.pipe(
    ofType(GET_PROJECTS_WITH_STATS),
    switchMap(() =>
      this.projectsService
        .projectsWithStats()
        .pipe(
          map((response: ProjectWithStatsResponse[]) => new GetProjectsWithStatsSuccessAction(response)),
          catchError(error => of(new GetProjectsWithStatsFailureAction(error)))
        )
    )
  );

  @Effect()
  getProject$: Observable<ProjectsAction> = this.actions$.pipe(
    ofType(GET_PROJECT),
    map((action: GetProjectAction) => action.payload),
    switchMap((payload: IdPayload) =>
      this.projectsService
        .singleProject(payload.id)
        .pipe(
          map(
            (project: SingleProjectResponse) =>
              new GetProjectSuccessAction(
                mapSingleProject(project, this.crazyCallService.account, this.crazyCallService.appId)
              )
          ),
          catchError(error => of(new GetProjectFailureAction(error)))
        )
    )
  );

  @Effect()
  createProject$: Observable<ProjectsAction> = this.actions$.pipe(
    ofType(CREATE_PROJECT),
    map((action: CreateProjectAction) => action.payload),
    switchMap((payload: Configuration) =>
      this.leadGenerationService
        .createProject(payload)
        .pipe(
          map((response: CreateProjectResponse) => new CreateProjectSuccessAction(response)),
          catchError(error => of(new CreateProjectFailureAction(error)))
        )
    )
  );

  @Effect()
  createDefaultConfiguration: Observable<ProjectsAction> = this.actions$.pipe(
    ofType(CREATE_DEFAULT_CONFIGURATION),
    map((action: CreateDefaultConfigurationAction) => action.payload),
    switchMap((payload: Configuration) =>
      this.leadGenerationService
        .saveDefaultConfiguration(payload)
        .pipe(
          map((response: HttpResponse<ConfigurationResponse>) => mapResponse(response.body)),
          map((config: Configuration) => new CreateDefaultConfigurationSuccessAction(config)),
          catchError(error => of(new CreateDefaultConfigurationFailureAction(error)))
        )
    )
  );

  @Effect({dispatch: false})
  afterDefaultConfiguration = this.actions$.pipe(
    ofType(CREATE_DEFAULT_CONFIGURATION_SUCCESS),
    map((action: CreateDefaultConfigurationSuccessAction) => action.payload),
    switchMap(() => this.accountService.accountConfiguredData$.map(x => x.data || EMPTY)), // redirect only when account is fully configured
    tap((config: Configuration) => {
      const projectId = config.projectId || 1;
      return this.zone.run(() => {
          this.router.navigate(['lead-generation', 'projects', projectId, 'settings'])
      })
    })
  );

  @Effect()
  changeProjectActivity: Observable<ProjectsAction> = this.actions$.pipe(
    ofType(CHANGE_ACTIVITY),
    map((action: ChangeActivityAction) => action.payload),
    switchMap((payload: ChangeProjectActivity) =>
      this.projectsService
        .changeActivity(payload.projectId, payload.active)
        .pipe(
          map((response: ChangeProjectActivity) => new ChangeActivitySuccessAction(response)),
          catchError(error => of(new ChangeActivityFailureAction(error)))
        )
    )
  );

  @Effect()
  updateProjectConfiguration: Observable<ProjectsAction> = this.actions$.pipe(
    ofType(UPDATE_PROJECT_CONFIGURATION),
    map((action: UpdateProjectConfigurationAction) => action.payload),
    switchMap((payload: Configuration) =>
      this.leadGenerationService
        .updateConfiguration(payload)
        .pipe(
          map((response: Configuration) => new UpdateProjectConfigurationSuccessAction(response)),
          catchError(error => of(new UpdateProjectConfigurationFailureAction(error)))
        )
    )
  );

  @Effect()
  updateProjectStyle: Observable<ProjectsAction> = this.actions$.pipe(
    ofType(UPDATE_PROJECT_STYLE),
    map((action: UpdateProjectStyleAction) => action.payload),
    switchMap((request: UpdateStyleRequest) =>
      this.leadGenerationStylesService
        .saveDb(request)
        .pipe(
          map((response: Configuration) => new UpdateProjectStyleSuccessAction(response)),
          catchError(error => of(new UpdateProjectStyleFailureAction(error)))
        )
    )
  );

  @Effect({ dispatch: false })
  projectCreationSuccess$: Observable<CreateProjectAction> = this.actions$.pipe(
    ofType(CREATE_PROJECT_SUCCESS),
    tap((response: CreateProjectAction) => {
      this.translate.get('LEAD_GENERATION.TOAST.PROJECT_ADDED').subscribe((res: string) => {
        this.toastr.success(res);
      });

      return this.zone.run(() =>
        this.router.navigateByUrl(`/lead-generation/projects/${response.payload.projectId}/creator/bubble`)
      );
    })
  );

  @Effect({ dispatch: false })
  updateProjectSuccess$: Observable<any> = this.actions$.pipe(
    ofType(UPDATE_PROJECT_CONFIGURATION_SUCCESS, UPDATE_PROJECT_STYLE_SUCCESS),
    tap(() => {
      this.translate.get('LEAD_GENERATION.TOAST.CONFIGURATION_UPDATED').subscribe((res: string) => {
        this.toastr.success(res);
      });
    })
  );

  @Effect({ dispatch: false })
  addOrigin$ = this.actions$.pipe(
    ofType(UPDATE_PROJECT_CONFIGURATION_SUCCESS, UPDATE_PROJECT_STYLE_SUCCESS),
    map((action: UpdateProjectConfigurationAction | UpdateProjectStyleAction) => action.payload),
    switchMap(
      (configuration: Configuration) =>
        configuration.domainName ? this.leadGenerationService.addOrigin(configuration.domainName) : EMPTY
    )
  );

  @Effect({ dispatch: false })
  getProjectSuccess$: Observable<any> = this.actions$.pipe(
    ofType(GET_PROJECT_FAILURE),
    tap(() => this.zone.run(() => this.router.navigate(['/lead-generation', 'projects'])))
  );

  @Effect({ dispatch: false })
  getProjectsSuccess$: Observable<any> = this.actions$.pipe(
    ofType(GET_PROJECTS_WITH_STATS_SUCCESS),
    map((action: GetProjectsWithStatsSuccessAction) => action.payload),
    // temporarily, until multiple projects are implemented
    tap((projects: ProjectWithStatsResponse[]) => {
      const path = projects.length ? ['projects', projects[0].id, 'installation'] : ['new-project', 'settings'];
      return this.zone.run(() => this.router.navigate(['/lead-generation', ...path]));
    })
  );

  @Effect()
  removeProject: Observable<any> = this.actions$.pipe(
    ofType(REMOVE_PROJECT),
    map((action: RemoveProjectAction) => action.payload),
    switchMap((payload: number) =>
      this.projectsService
        .removeProject(payload)
        .pipe(
          map(() => new RemoveProjectSuccessAction()),
          catchError(error => of(new RemoveProjectFailureAction(error)))
        )
    )
  );

  @Effect({ dispatch: false })
  removeProjectAction$: Observable<any> = this.actions$.pipe(
    ofType(REMOVE_PROJECT_SUCCESS),
    tap(() => {
      this.translate.get('LEAD_GENERATION.TOAST.PROJECT_REMOVED').subscribe((res: string) => {
        this.toastr.success(res);
      });

      return this.zone.run(() => this.router.navigate(['/lead-generation', 'projects']));
    })
  );

  constructor(
    private actions$: Actions,
    private toastr: ToastrService,
    private leadGenerationService: LeadGenerationConfigurationService,
    private leadGenerationStylesService: LeadGenerationStylesService,
    private projectsService: ProjectsService,
    private crazyCallService: CrazyCallConfigService,
    private router: Router,
    private zone: NgZone,
    private translate: TranslateService,
    private accountService: AccountService
  ) {}
}
